import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Grid } from "@mantine/core";
import { useGetInspections } from "@/services/Inspections";
import { useInspectionsStore } from "@/stores/inspectionsStore";
import DatePickerInput from "@/components/atoms/DatePickerInput";
import Select from "@/components/atoms/Select";
import styles from "./styles.module.css";

/** Planned date, supplier and fraction filters above the Inspections table. */
export default function InspectionsFilter() {
  const { t } = useTranslation();
  const from = useInspectionsStore((s) => s.from);
  const setFrom = useInspectionsStore((s) => s.setFrom);
  const to = useInspectionsStore((s) => s.to);
  const setTo = useInspectionsStore((s) => s.setTo);
  const supplierCompanyId = useInspectionsStore((s) => s.supplierCompanyId);
  const setSupplierCompanyId = useInspectionsStore((s) => s.setSupplierCompanyId);
  const salesItemId = useInspectionsStore((s) => s.salesItemId);
  const setSalesItemId = useInspectionsStore((s) => s.setSalesItemId);

  // The options come from the unfiltered list, so picking one filter doesn't empty the others.
  const { data: inspections = [] } = useGetInspections({});
  const { supplierOptions, fractionOptions } = useMemo(() => {
    const suppliers = new Map<number, string>();
    const fractions = new Map<number, string>();
    inspections.forEach((inspection) => {
      suppliers.set(inspection.supplierCompanyId, inspection.supplierCompanyName);
      if (inspection.salesItemName) fractions.set(inspection.salesItemId, inspection.salesItemName);
    });
    const toOptions = (entries: Map<number, string>) =>
      [...entries]
        .map(([value, label]) => ({ value: String(value), label }))
        .sort((a, b) => a.label.localeCompare(b.label));
    return { supplierOptions: toOptions(suppliers), fractionOptions: toOptions(fractions) };
  }, [inspections]);

  return (
    <Grid className={styles.filter} align="end">
      <Grid.Col span={{ base: 12, sm: 6, lg: 2 }}>
        <DatePickerInput
          label={t("inspections:filter.from")}
          value={from}
          onChange={(value) => setFrom(value ? new Date(value) : null)}
          maxDate={to ?? undefined}
          clearable
          data-testid="inspections-filter-from"
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 6, lg: 2 }}>
        <DatePickerInput
          label={t("inspections:filter.to")}
          value={to}
          onChange={(value) => setTo(value ? new Date(value) : null)}
          minDate={from ?? undefined}
          clearable
          data-testid="inspections-filter-to"
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 6, lg: 2 }}>
        <Select
          label={t("inspections:filter.supplier")}
          data={supplierOptions}
          value={supplierCompanyId === null ? null : String(supplierCompanyId)}
          onChange={(value) => setSupplierCompanyId(value ? Number(value) : null)}
          clearable
          data-testid="inspections-filter-supplier"
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 6, lg: 2 }}>
        <Select
          label={t("inspections:filter.fraction")}
          data={fractionOptions}
          value={salesItemId === null ? null : String(salesItemId)}
          onChange={(value) => setSalesItemId(value ? Number(value) : null)}
          clearable
          data-testid="inspections-filter-fraction"
        />
      </Grid.Col>
    </Grid>
  );
}
